import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Calendar, Clock, MapPin, Users, Download, QrCode } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

const RiderTripSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // Get trip data from navigation state
  const tripData = location.state;

  // Redirect if no trip data
  useEffect(() => {
    if (!tripData) {
      navigate('/');
    }
    
    // Scroll to top on mobile when component mounts
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [tripData, navigate]);
  if (!tripData) {
    return null;
  }
  const handleAddToWallet = () => {
    // TODO: Implement Apple Wallet integration
    // This would generate a PKPass file and trigger the download
    toast({
      title: "Feature Coming Soon",
      description: "Apple Wallet integration will be available in a future update"
    });
  };
  const handleBackToHome = () => {
    navigate('/');
  };

  // Generate a mock confirmation number
  const confirmationNumber = `PB${Date.now().toString().slice(-6)}`;
  return <RiderLayout>
      <div className="container mx-auto px-4 py-8 max-w-md">
        {/* Success Animation */}
        <motion.div className="text-center mb-8" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }}>
          <div className="w-20 h-20 bg-gradient-to-r from-[#2BFF00] to-[#31FFE7] rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-black mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600">Your party bus has been successfully booked</p>
        </motion.div>

        {/* Ticket Preview Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }}>
          <h2 className="text-center text-2xl font-bold text-black mb-6">Ticket Preview</h2>
          
          {/* Ticket Card */}
          <Card className="overflow-hidden shadow-lg border-0">
            <CardContent className="p-0">
              {/* Neon Green to Blue Gradient Header Section with Black Overlay */}
              <div className="bg-gradient-to-r from-[#2BFF00] to-[#31FFE7] text-white p-6 relative">
                {/* Black overlay with 20% opacity */}
                <div className="absolute inset-0 bg-black opacity-20"></div>
                
                {/* Decorative circles */}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white rounded-full -ml-4 z-10"></div>
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white rounded-full -mr-4 z-10"></div>
                
                <div className="space-y-4 relative z-10">
                  <div>
                    <h3 className="text-2xl font-bold">{tripData.eventName}</h3>
                    <p className="text-white/80">Party Bus Service</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5" />
                      <span>{tripData.date || 'TBD'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5" />
                      <span>{tripData.startTime || 'TBD'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* White Content Section */}
              <div className="bg-white p-6 space-y-6">
                {/* Venue */}
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold text-black">Venue</p>
                    <p className="text-gray-600">{tripData.to}</p>
                  </div>
                </div>

                {/* Ticket Type */}
                <div className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold text-black">Capacity</p>
                    <p className="text-gray-600">10 People</p>
                  </div>
                </div>

                {/* Pickup Time */}
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold text-black">Pickup Time</p>
                    <p className="text-gray-600">{tripData.startTime || 'TBD'}</p>
                  </div>
                </div>

                {/* QR Code Section */}
                <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <QrCode className="w-8 h-8 text-gray-600" />
                    <div>
                      <p className="font-semibold text-black">Scan for</p>
                      <p className="text-gray-600 text-sm">Trip Details</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Presented By</p>
                    <p className="font-semibold text-black">DISCO</p>
                  </div>
                </div>

                {/* Confirmation Number */}
                <div className="text-center pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">Confirmation #</p>
                  <p className="text-lg font-mono font-bold text-black">{confirmationNumber}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div className="mt-8 space-y-4" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }}>
          <Button onClick={handleAddToWallet} className="w-full bg-black text-white py-6 rounded-xl text-lg font-semibold hover:bg-gray-800 transition-all duration-200 flex items-center justify-center gap-3">
            <Download className="w-5 h-5" />
            Add to Apple Wallet
          </Button>
          
          <Button variant="outline" onClick={handleBackToHome} className="w-full py-6 rounded-xl text-lg font-semibold border-2 border-gray-300 hover:bg-gray-50 transition-all duration-200">
            Share Confirmation With Friends
          </Button>
        </motion.div>

        {/* Additional Info */}
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-black mb-3">Important Information</h3>
          <div className="text-sm text-gray-600 space-y-2">
            <p>• Your driver will contact you 30 minutes before pickup</p>
            <p>• Please be ready 10 minutes before your scheduled pickup time</p>
            <p>• Keep this confirmation handy for your trip</p>
            <p>• For support, contact us at support@discoveryconciergeservices.com</p>
          </div>
        </div>
      </div>
    </RiderLayout>;
};
export default RiderTripSuccess;
